/**
 * BeamLinkStore — a tiny ring buffer of beam-link endpoint samples
 * (beam-builders spec §7). BeamLinkSystem records ~4Hz {from, to} rows per
 * live caster→target link; the ability-capture sidecar reads the recent
 * ones so the verifier's `motion:'beam'` checks have real endpoint data
 * (a linked beam's shape position is only its caster-end origin — the
 * `effects[]` rows can't prove the link reached anything).
 *
 * Singleton + ring buffer, mirroring ChainSegmentStore's shape. Pure data;
 * no Pixi/window. Cheap enough to always run (~4Hz per live link).
 */
import type { BeamSample } from '../../shared/diagnostics/CaptureSidecarTypes';

const MAX_SAMPLES = 60;

class BeamLinkStoreImpl {
  private buffer: BeamSample[] = [];

  record(sample: BeamSample): void {
    this.buffer.push(sample);
    if (this.buffer.length > MAX_SAMPLES) this.buffer.shift();
  }

  /** All buffered samples (newest last). */
  all(): readonly BeamSample[] {
    return this.buffer;
  }

  /** Samples at/after a timestamp floor — the cast-window slice. */
  since(tsFloor: number): BeamSample[] {
    return this.buffer.filter((s) => s.ts >= tsFloor);
  }

  clear(): void {
    this.buffer = [];
  }
}

export const BeamLinkStore = new BeamLinkStoreImpl();
